// src/App.js
import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { guardarDestino, consumirDestino } from './utils/destinoTrasLogin';

import Login from './pages/Login';
import Contratar from './pages/Contratar';
import Dashboard from './pages/Dashboard';
import ContratarAddon from './pages/ContratarAddon';
import Bots from './pages/Bots';
import Chats from './componentes/Chats';
import ModulosActivos from './componentes/ModulosActivos';
import TestChatGPT from './pages/TestChatGPT';
import ChatDetalle from './componentes/ChatDetalle';
import SesionDeBandeja from './contexto/SesionDeBandeja';
import ValidarEnlace from './pages/ValidarEnlace';
import UpdateBanner from './componentes/UpdateBanner';
import { crearDetector } from './utils/deteccionDeVersion';
import PaymentStatusBanner from './componentes/PaymentStatusBanner';
import Footer from './components/Footer';
import ConsentGate from './components/ConsentGate';
import OrgResolutionError from './components/OrgResolutionError';
import ElegirOrganizacion from './components/organization/ElegirOrganizacion';
import ConnectWACloudWizard from './pages/ConnectWACloudWizard';
import WACloudNumbers from './pages/WACloudNumbers';
import WACloudTemplates from './pages/WACloudTemplates';
import WACloudQuality from './pages/WACloudQuality';
import Integrations from './pages/Integrations';
import ExportDemo from './pages/ExportDemo';
import ChatsExport from './pages/ChatsExport';
import MultiChannels from './pages/MultiChannels';
import ConfigAgente from './componentes/ConfigAgente';
import BotWizard from './pages/BotWizard';
import BotConfig from './pages/BotConfig';
import Canales from './pages/Canales';
import Statistics from './pages/Statistics';
import Billing from './pages/Billing';
import OrganizationSettings from './pages/organization/OrganizationSettings';
import AcceptInvitation from './pages/organization/AcceptInvitation';
import ConectarAgenda, { AgendaConectada } from './pages/ConectarAgenda';
import MyTeam from './pages/organization/MyTeam';
import Campanas from './pages/Campanas';
import AdsStudio from './pages/AdsStudio';

// ✅ Importa api para exponerla en window
import api from './services/api';

// ✅ Importa OrganizationProvider
import { OrganizationProvider } from './context/OrganizationContext';
import { ToastProvider } from './components/Toast';

import { useFirestoreIdleManager } from './hooks/useFirestoreIdleManager';
import { useLanguageSync } from './i18n/useLanguageSync';
import { useOrganization } from './context/OrganizationContext';
import { useTranslation } from 'react-i18next';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// 🔑 Detectar versión antigua y limpiar caches
// ═══════════════════════════════════════════════════════════════════════════════
//  DETECCIÓN DE VERSIÓN NUEVA — lo que aquí había NO PODÍA FUNCIONAR.
//
//  Era `CURRENT_VERSION = '1.0.2'`, una constante escrita a mano comparada contra
//  `localStorage` en el montaje. Dos fallos independientes, cada uno suficiente: la
//  constante no cambiaba con el despliegue —su último cambio fue en un commit de
//  exportaciones— y sólo se evaluaba al montar, que es justo el caso que había que
//  cubrir: una pestaña YA abierta.
//
//  El 21-sep-2026 costó que tres bloqueos de contacto salieran con el comportamiento
//  viejo cuatro minutos después de desplegar su arreglo.
//
//  Ahora el sello lo pone el BUILD (`tools/sellar-build.mjs`) y la comparación es
//  contra `/version.json`. La regla y los disparadores viven en
//  `utils/deteccionDeVersion.js`, aparte, para poder probarlos sin montar React.
// ═══════════════════════════════════════════════════════════════════════════════
function useVersionCheck(alDetectar) {
  useEffect(() => {
    const detector = crearDetector({
      fetchImpl: (...a) => fetch(...a),
      alDetectar,
    });

    // Al montar: cubre la pestaña que se abre justo después de un despliegue.
    detector.consultar('montaje');

    const visible = () => document.visibilityState === 'visible';
    const alVolver = (motivo) => () => { if (visible()) detector.consultar(motivo); };
    const porVisibilidad = alVolver('visibilitychange');
    const porFoco = alVolver('focus');

    // LOS DOS, y el antirrebote de `crearDetector` evita la petición doble cuando
    // llegan juntos: `visibilitychange` no dispara si el operador cambia a otra
    // aplicación con el navegador visible en otro monitor y vuelve haciendo clic.
    document.addEventListener('visibilitychange', porVisibilidad);
    window.addEventListener('focus', porFoco);

    const id = setInterval(() => { if (visible()) detector.consultar('periodico'); }, detector.intervaloMs);

    return () => {
      document.removeEventListener('visibilitychange', porVisibilidad);
      window.removeEventListener('focus', porFoco);
      clearInterval(id);
    };
  }, [alDetectar]);
}

/**
 * Manda al login guardando a dónde iba, con su query string.
 *
 * El guardado va en el cuerpo del render y no en un `useEffect` porque
 * `<Navigate>` navega DESDE un efecto: en un efecto propio la carrera es
 * innecesaria. Es idempotente —escribe la misma clave con el mismo valor— así
 * que un doble render en StrictMode no hace daño.
 */
function AlLoginConservandoDestino() {
  const location = useLocation();
  guardarDestino(location.pathname + location.search);
  return <Navigate to="/" replace />;
}

/**
 * Tras el login, al destino guardado; si no hay, al dashboard.
 *
 * El destino se consume UNA sola vez, en el inicializador de `useState`. Leerlo
 * en el cuerpo del render lo borraría en el primer render y devolvería
 * `/dashboard` en el segundo, cambiando el destino a mitad de navegación.
 */
function RedirigirTrasLogin() {
  const [destino] = useState(() => consumirDestino() || '/dashboard');
  return <Navigate to={destino} replace />;
}

function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authInit, setAuthInit] = useState(false);
  const [user, setUser] = useState(null);
  const { t } = useTranslation('common');
  const { needsConsent, needsOnboarding, needsOrgChoice, orgsResolved, orgError, retryOrgResolution } = useOrganization();
  useLanguageSync();

  useEffect(() => {
    // Persistencia ya configurada en firebase.js via initializeAuth
    // onAuthStateChanged espera a que la sesión se restaure de IndexedDB antes de disparar
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthInit(true);
    });

    return unsubscribe;
  }, [navigate]);

  // Org resolution failed after retries → error screen (retry / sign out), never
  // an infinite loader.
  if (user && orgError) {
    return <OrgResolutionError onRetry={retryOrgResolution} />;
  }

  // Gate de carga - no mostrar rutas hasta que auth se inicialice, y (para un
  // usuario autenticado) hasta que la resolución de org termine DE VERDAD. Se
  // espera orgsResolved (no !loading): orgsResolved se resetea a false al inicio
  // de cada pase de resolución, así el gate nunca evalúa needsConsent/needsOnboarding
  // sobre un context intermedio (emptyContext transitorio) — la causa del bounce
  // a /contratar.
  if (!authInit || (user && !orgsResolved)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '1.2rem'
      }}>
        {t('initializing')}
      </div>
    );
  }

  // Legal consent gate: block the whole app (both auth paths) until the user has
  // accepted the current legal version. Reachable only once orgsResolved is true.
  if (user && needsConsent) {
    return <ConsentGate />;
  }

  // Elección de organización: pertenece a varias y no hay ninguna guardada que aplicar.
  // VA ANTES DE LA PUERTA DE ONBOARDING y después del consentimiento. El orden no es
  // estético: sin organización resuelta, el onboarding lo mandaría a /contratar y esta
  // pantalla no se vería nunca (por eso `needsOnboarding` ya excluye este caso en el
  // contexto; esto es la otra mitad).
  //
  // Se exime en las MISMAS rutas que el onboarding, reutilizando `isOnboardingExempt`: un
  // invitado en /accept-invitation/* no debe toparse con un selector, y quien vuelve de
  // Stripe con ?session_id= tiene una organización recién creada en camino — ahí manda el
  // `waitingForOrg` de Dashboard.js, que espera al webhook y entra en la nueva.
  if (user && needsOrgChoice && !isOnboardingExempt(location) && !hasPendingInvite()) {
    return <ElegirOrganizacion />;
  }

  // Onboarding gate (Bloque 5): a user who accepted consent but has no organization
  // is funneled to /contratar (crear org + plan → Stripe → el webhook crea la org).
  // Keyed on org membership (D1), NUNCA en billing status (R2: un usuario en dunning
  // TIENE org, no debe caer). Excluye: destino + rutas de invitación/auth (R3),
  // invitados en tránsito (R1) y la ventana post-checkout (session_id → Dashboard
  // está polleando la creación de la org vía webhook). Reachable solo con orgsResolved.
  if (user && needsOnboarding && !isOnboardingExempt(location) && !hasPendingInvite()) {
    return <Navigate to="/contratar" replace />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={user ? <RedirigirTrasLogin /> : <Login />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" replace />} />
        {/* Sin sesión conserva su destino con el querystring (`?intent=contratar&plan=…` de la
            landing): el plan elegido llega a /contratar y el login sabe que viene a contratar. */}
        <Route path="/contratar" element={user ? <Contratar /> : <AlLoginConservandoDestino />} />
        {/* Con /contratar, las rutas que conservan su destino a través del login, porque se
            llega a ellas desde fuera de la app —el correo de cuota, con `?addon=`, y la landing—.
            También la bandeja (abajo), por el correo de una derivación. Las demás se alcanzan
            navegando, ya con sesión. */}
        <Route path="/addon" element={user ? <ContratarAddon /> : <AlLoginConservandoDestino />} />
        <Route path="/bots" element={user ? <Bots /> : <Navigate to="/" replace />} />
        {/* ═══════════════════════════════════════════════════════════════════
            RUTA DE LAYOUT · la memoria de sesión de la bandeja.

            `/chats` y `/chat/:chatID` eran rutas HERMANAS, así que abrir un chat
            DESMONTABA `Chats` y se perdían los siete filtros, la búsqueda, el rango de
            fechas, el panel abierto y la posición del scroll. Envolviéndolas, el
            proveedor sobrevive el viaje.

            ⚠️ `ChatDetalle` va DENTRO y NO consume el contexto. No es un descuido: tiene
            que estar dentro para que el proveedor no se desmonte al abrir un chat, que
            es justamente el problema que esto resuelve.

            Y que `/dashboard` quede FUERA es lo que hace que volver de allí empiece
            limpio, sin escribir una sola línea de reseteo.
            ═══════════════════════════════════════════════════════════════════ */}
        {/* Sin sesión conserva su destino (29-oct-2026): el correo de una derivación enlaza a
            `/chat/<id>?org=`, y el dueño lo abre en el móvil, sin sesión, igual que el de cuota. */}
        <Route element={user ? <SesionDeBandeja /> : <AlLoginConservandoDestino />}>
          <Route path="/chats" element={<Chats />} />
          <Route path="/chat/:chatID" element={<ChatDetalle />} />
        </Route>
        <Route path="/modulos" element={user ? <ModulosActivos /> : <Navigate to="/" replace />} />
        <Route path="/chatgpt" element={user ? <TestChatGPT /> : <Navigate to="/" replace />} />
        <Route path="/enlace" element={<ValidarEnlace />} />
        <Route path="/connect/whatsapp-cloud" element={user ? <ConnectWACloudWizard /> : <Navigate to="/" replace />} />
        <Route path="/whatsapp-cloud/numbers" element={user ? <WACloudNumbers /> : <Navigate to="/" replace />} />
        <Route path="/whatsapp-cloud/templates" element={user ? <WACloudTemplates /> : <Navigate to="/" replace />} />
        <Route path="/whatsapp-cloud/quality" element={user ? <WACloudQuality /> : <Navigate to="/" replace />} />
        <Route path="/integrations" element={user ? <Integrations /> : <Navigate to="/" replace />} />
        <Route path="/export-demo" element={user ? <ExportDemo /> : <Navigate to="/" replace />} />
        <Route path="/chats-export" element={user ? <ChatsExport /> : <Navigate to="/" replace />} />
        <Route path="/multi" element={user ? <MultiChannels /> : <Navigate to="/" replace />} />
        <Route path="/canales-multiples" element={user ? <MultiChannels /> : <Navigate to="/" replace />} />
        <Route path="/agente" element={user ? <ConfigAgente botId="default" /> : <Navigate to="/" replace />} />
        <Route path="/bot/:botId/crear" element={user ? <BotWizard /> : <Navigate to="/" replace />} />
        <Route path="/bot/:botId/configuracion" element={user ? <BotConfig /> : <Navigate to="/" replace />} />
        <Route path="/canales" element={user ? <Canales /> : <Navigate to="/" replace />} />
        {/* /statistics retired from UI — redirect deep-links to dashboard. Statistics.jsx,
            its import (line ~34) and statistics.json locales remain as cleanup candidates. */}
        <Route path="/statistics" element={<Navigate to="/dashboard" replace />} />
        <Route path="/billing" element={user ? <Billing /> : <Navigate to="/" replace />} />
        <Route path="/mi-equipo" element={user ? <MyTeam /> : <Navigate to="/" replace />} />
        <Route path="/campanas" element={user ? <Campanas /> : <Navigate to="/" replace />} />
        <Route path="/ads-studio" element={user ? <AdsStudio /> : <Navigate to="/" replace />} />
        <Route path="/organizations/:orgId/settings" element={user ? <OrganizationSettings /> : <Navigate to="/" replace />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
        {/* FASE 3b — públicas y sin sesión: el profesional que conecta su calendario no es
            usuario de PUX. Sin `user ? … : Navigate`, a propósito: redirigir a login sería
            pedirle una cuenta que la fase entera existe para no pedirle. */}
        <Route path="/conectar-agenda/:token" element={<ConectarAgenda />} />
        <Route path="/agenda-conectada" element={<AgendaConectada />} />
      </Routes>
      {!hideFooterOn(location.pathname) && <Footer />}
    </>
  );
}

// Routes where the onboarding gate must NOT redirect a no-org user:
//  - /contratar            : the destination (else infinite redirect loop)
//  - /accept-invitation/*  : invited user joining someone else's org (R1/R3)
//  - /conectar-agenda/*    : professional connecting a calendar; NOT a PUX user (3b)
//  - /agenda-conectada     : the "you can close this" page for the same flow
//  - /enlace               : email-link sign-in callback (auth in progress)
//  - ?session_id=...       : post-Stripe return; the org is being created by the
//                            webhook and Dashboard's waitingForOrg is polling for it.
// OAuth callbacks (/api/oauth/*) and /deletion-status are hosting rewrites to Cloud
// Functions — they never reach this SPA router, so they need no entry here.
function isOnboardingExempt(location) {
  const p = location.pathname;
  if (p === '/contratar' || p.startsWith('/accept-invitation/') || p === '/enlace') return true;
  // 3b: quien conecta una agenda no tiene organización NI cuenta. La puerta de onboarding
  // lo mandaría a contratar un plan.
  if (p.startsWith('/conectar-agenda/') || p === '/agenda-conectada') return true;
  if (new URLSearchParams(location.search).get('session_id')) return true;
  return false;
}

// Invited user in transit: mismo mecanismo que Dashboard.js usa para no redirigir
// antes de que se procese la invitación.
function hasPendingInvite() {
  try { return !!localStorage.getItem('pendingInviteToken'); } catch { return false; }
}

// The legal footer is mounted globally, but a few routes lock the viewport to
// height:100vh with their own internal scroll (chat detail, chatgpt). On those,
// a flow-sibling footer would add a dead page scrollbar and hang below the fold,
// so we hide it there. Note: '/chat/' (detail) is excluded but '/chats' (inbox,
// normal flow) keeps the footer.
// ⚠️ MAINTENANCE: if you add a new route whose root uses a fixed `height:100vh`
// (not min-height), add its path here or the footer will force a page scrollbar.
function hideFooterOn(pathname) {
  return pathname.startsWith('/chat/') || pathname === '/chatgpt';
}

function App() {
  // `waitingWorker` se RETIRÓ con su setter: tenía 0 invocaciones y colgaba del SW
  // kill-switch, que se desregistra solo. Ver `deteccionDeVersion.js`.
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useVersionCheck(useCallback(() => setUpdateAvailable(true), []));
  useFirestoreIdleManager();

  // SW lifecycle is handled by the kill-switch SW itself (registered in index.js):
  // it claims, clears caches, and unregisters in its activate handler. No page-side
  // unregister here — that would race/fight the registration.

  // OFRECE, NO FUERZA. Un `reload()` automático sobre un agente a medio escribir le
  // borra el texto, y ese daño es peor que el que se evita.
  const updateApp = () => window.location.reload();

  // ✅ Exponer API globalmente para pruebas en consola
  useEffect(() => {
    window.api = api;
    console.log('✅ API expuesta en window.api', api);
    // 👇 debug: ver variables activas en consola
    console.log('🔧 REACT_APP_WA_CLOUD_MODE =', process.env.REACT_APP_WA_CLOUD_MODE);
    console.log('🔧 REACT_APP_FIREBASE_FUNCTIONS_URL =', process.env.REACT_APP_FIREBASE_FUNCTIONS_URL);
  }, []);

  return (
    <ToastProvider>
      <OrganizationProvider>
        <Router>
          {updateAvailable && <UpdateBanner onUpdate={updateApp} />}
          <PaymentStatusBanner />
          <AuthHandler />
        </Router>
      </OrganizationProvider>
    </ToastProvider>
  );
}

export default App;